import React, { useEffect, useState } from "react";
import { getAutopilotStatus, updateAutopilotConfig, runAutopilotNow, resetAutopilot } from "../lib/api";
import { Robot, Clock, Play, ArrowClockwise, CheckCircle, PauseCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const MODE_LABELS = {
  "sell-installers":     "Buyers ← Installer lists",
  "sell-manufacturers":  "Installers ← Manufacturer lists",
};

const TONE_OPTIONS = [
  { value: "friendly",      label: "Friendly" },
  { value: "professional",  label: "Professional" },
  { value: "punchy",        label: "Punchy" },
  { value: "consultative",  label: "Consultative" },
];

export const AutopilotCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState({});

  const load = async () => {
    try { setStatus(await getAutopilotStatus()); } catch {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  if (!status) return null;

  const cfg = { ...status.config, ...editing };
  const state = status.state || {};
  const nextCountry = status.next_country;
  const nextRunCet = new Date(status.next_run_at_cet);
  const nextRunLabel = nextRunCet.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin",
  }) + " CET";
  const enabled = cfg.enabled;

  const persist = async (patch) => {
    setSaving(true);
    try {
      const next = await updateAutopilotConfig(patch);
      setStatus((s) => ({ ...s, config: next }));
      setEditing((e) => {
        const c = { ...e };
        for (const k of Object.keys(patch)) delete c[k];
        return c;
      });
    } catch (e) {
      toast.error("Update failed");
    }
    setSaving(false);
  };

  const toggle = async () => {
    await persist({ enabled: !enabled });
    toast.success(!enabled ? `Autopilot ON · next: ${nextCountry?.flag} ${nextCountry?.name} at 09:00 CET` : "Autopilot OFF");
  };

  const runNow = async () => {
    if (!window.confirm(`Send ${cfg.cap} pitches to ${nextCountry?.name} RIGHT NOW? This will trigger a real email batch.`)) return;
    setBusy(true);
    try {
      const res = await runAutopilotNow();
      toast.success(`Autopilot fired for ${res.country_code} · ${res.cap} emails queued`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    }
    setBusy(false);
  };

  const resetRotation = async () => {
    if (!window.confirm("Reset country rotation — next country will be Austria again. Continue?")) return;
    setBusy(true);
    try {
      await resetAutopilot();
      toast.success("Rotation reset — next country: Austria");
      load();
    } catch { toast.error("Reset failed"); }
    setBusy(false);
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const dirty = Object.keys(editing).length > 0;
  const commit = async () => { await persist(editing); toast.success("Saved"); };

  return (
    <div className={`border-2 ${enabled ? "border-[#0047FF] shadow-[3px_3px_0px_#0A0A0A]" : "border-black bg-white"} p-4 mb-6`} data-testid="autopilot-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-[240px]">
          <div className={`w-10 h-10 flex items-center justify-center ${enabled ? "bg-[#0047FF] text-white" : "bg-[#0A0A0A] text-[#D4FF00]"}`}>
            <Robot size={20} weight="bold" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0047FF]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                AUTOPILOT
              </div>
              {enabled ? (
                <span className="inline-flex items-center gap-1 bg-[#D4FF00] text-[#0A0A0A] border border-black px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  <CheckCircle size={9} weight="bold" /> ACTIVE
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 bg-white border border-black/40 text-[#525252] px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  <PauseCircle size={9} weight="bold" /> OFF
                </span>
              )}
            </div>
            <div className="text-lg tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
              Sends {cfg.cap} pitches/day · rotating {status.eu_country_count} EU countries · {String(cfg.hour_cet).padStart(2, "0")}:{String(cfg.minute_cet).padStart(2, "0")} CET
            </div>
            <div className="text-sm text-[#525252] mt-1 flex items-center gap-1.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              <Clock size={12} weight="bold" />
              Next: <b>{nextCountry?.flag} {nextCountry?.name}</b> · {nextRunLabel}
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <button
            data-testid="autopilot-toggle"
            onClick={toggle}
            disabled={saving}
            className={`${enabled ? "bg-[#0A0A0A] text-white" : "bg-[#0047FF] text-white"} px-3 py-2 text-xs tracking-[0.2em] uppercase shadow-[2px_2px_0px_${enabled ? "#525252" : "#0A0A0A"}] hover:opacity-90 active:translate-y-[2px] active:translate-x-[2px] active:shadow-none disabled:bg-[#525252] disabled:shadow-none inline-flex items-center gap-1.5 transition-all duration-150`}
            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
          >
            {enabled ? <><PauseCircle size={12} weight="bold" /> PAUSE</> : <><Play size={12} weight="bold" /> ENABLE</>}
          </button>
          <button
            data-testid="autopilot-run-now"
            onClick={runNow}
            disabled={busy}
            className="border border-black bg-white px-3 py-2 text-xs tracking-[0.2em] uppercase hover:bg-black/5 disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
            title="Fire the batch right now (for testing)"
          >
            <Play size={12} weight="bold" /> RUN NOW
          </button>
        </div>
      </div>

      {/* Config row */}
      <div className="mt-4 pt-4 border-t border-black/15 grid grid-cols-2 md:grid-cols-5 gap-3">
        <ConfigField label="DAILY CAP">
          <input
            type="number" min={1} max={500}
            value={cfg.cap}
            onChange={(e) => set("cap", parseInt(e.target.value, 10) || 1)}
            data-testid="autopilot-cap"
            className="w-full border border-black/25 px-2 py-1.5 text-sm bg-white outline-none focus:border-black"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          />
        </ConfigField>
        <ConfigField label="SEND AT (CET)">
          <div className="flex items-center gap-1">
            <input
              type="number" min={0} max={23}
              value={cfg.hour_cet}
              onChange={(e) => set("hour_cet", Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
              data-testid="autopilot-hour"
              className="w-14 border border-black/25 px-2 py-1.5 text-sm bg-white outline-none focus:border-black"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            />
            <span className="text-[#525252]">:</span>
            <input
              type="number" min={0} max={59}
              value={cfg.minute_cet}
              onChange={(e) => set("minute_cet", Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)))}
              data-testid="autopilot-minute"
              className="w-14 border border-black/25 px-2 py-1.5 text-sm bg-white outline-none focus:border-black"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            />
          </div>
        </ConfigField>
        <ConfigField label="AUDIENCE">
          <select
            value={cfg.mode}
            onChange={(e) => set("mode", e.target.value)}
            data-testid="autopilot-mode"
            className="w-full border border-black/25 px-2 py-1.5 text-sm bg-white"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {Object.entries(MODE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </ConfigField>
        <ConfigField label="TONE">
          <select
            value={cfg.tone}
            onChange={(e) => set("tone", e.target.value)}
            data-testid="autopilot-tone"
            className="w-full border border-black/25 px-2 py-1.5 text-sm bg-white"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {TONE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </ConfigField>
        <ConfigField label="ACTIONS">
          <div className="flex gap-1">
            {dirty ? (
              <button
                data-testid="autopilot-save"
                onClick={commit}
                disabled={saving}
                className="flex-1 bg-[#0047FF] text-white px-2 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-[#0038CC] disabled:bg-[#525252]"
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
              >
                {saving ? "SAVING…" : "SAVE"}
              </button>
            ) : (
              <button
                data-testid="autopilot-reset-rotation"
                onClick={resetRotation}
                disabled={busy}
                className="flex-1 border border-black/25 px-2 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-black/5"
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                title="Reset country rotation back to Austria"
              >
                <ArrowClockwise size={10} weight="bold" /> RESET
              </button>
            )}
          </div>
        </ConfigField>
      </div>

      {/* Last-run summary */}
      {state.last_country_code && (
        <div className="mt-3 pt-3 border-t border-black/10 text-xs text-[#525252] flex items-center gap-3 flex-wrap" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          <span>Runs: <b>{state.runs || 0}</b></span>
          <span>·</span>
          <span>Last country: <b>{state.last_country_code}</b></span>
          {state.last_run_at && (
            <>
              <span>·</span>
              <span>Last run: {new Date(state.last_run_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </>
          )}
          {state.last_batch_id && (
            <>
              <span>·</span>
              <a href="/history" className="text-[#0047FF] hover:underline">See sent history →</a>
            </>
          )}
          {state.last_error && (
            <span className="inline-flex items-center gap-1 text-red-700">
              <Warning size={10} weight="bold" /> {state.last_error}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const ConfigField = ({ label, children }) => (
  <div>
    <div className="text-[9px] tracking-[0.2em] uppercase text-[#525252] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>{label}</div>
    {children}
  </div>
);
